import { useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  ModalFooter,
  SimpleGrid,
  useToast,
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  Flex,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  CREATE_PAGE_GROUP,
  REMOVE_GROUP_ELEMENT,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import sanitizeString from '../../../utils/sanitizeString';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import context from '../context';

export const redirectTitleTypes = [
  {
    value: 'default',
    label: 'Normal',
  },
  {
    value: 'with-image',
    label: 'Com imagem',
  },
  {
    value: 'with-search',
    label: 'Com caixa de pesquisa',
  },
];

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  name: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
  padding_top?: number;
  padding_bottom?: number;
  padding_right?: number;
  padding_left?: number;
  border_top?: string;
  border_bottom?: string;
  border_left?: string;
  border_right?: string;
  height?: string;
  type: {
    label: string | string;
    value: string | string;
  };
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  type: yup
    .object()
    .shape({
      value: yup.string().required('El tipo es obligatorio'),
      label: yup.string().required('El tipo es obligatorio'),
    })
    .required('El tipo es obligatorio'),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('La fecha de inicio es obligatoria'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading }] = useMutation(
    CREATE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [updatePageGroup, { loading: updateLoading }] = useMutation(
    UPDATE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [removeGroupElement, { loading: removeLoading }] = useMutation(
    REMOVE_GROUP_ELEMENT,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [removePageGroup, { loading: deleteLoading }] = useMutation(
    REMOVE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;

  useEffect(() => {
    if (pageGroup) {
      setValue(
        'type',
        redirectTitleTypes.find(type => type?.value === pageGroup?.type),
      );
      if (pageGroup?.schedule_end) {
        setValue('schedule', true);
        setValue('schedule_start', new Date(pageGroup.schedule_start));
        setValue('schedule_end', new Date(pageGroup.schedule_end));
        setValue('countdown', pageGroup.countdown);
      }
      setValue('name', pageGroup.name);
      setValue('padding_top', pageGroup.padding_top);
      setValue('padding_right', pageGroup.padding_right);
      setValue('padding_left', pageGroup.padding_left);
      setValue('padding_bottom', pageGroup.padding_bottom);
      setValue('height', pageGroup.height);
      setValue('border_top', pageGroup?.border_top);
      setValue('border_bottom', pageGroup?.border_bottom);
      setValue('border_left', pageGroup?.border_left);
      setValue('border_right', pageGroup?.border_right);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageGroup]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              id: pageGroup.id,
              schedule_end: scheduleData ? rest.schedule_end : null,
              countdown: scheduleData ? rest.countdown : false,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
              type: data.type?.value,
              padding_top: Number(data.padding_top),
              padding_bottom: Number(data.padding_bottom),
              padding_right: Number(data.padding_right),
              padding_left: Number(data.padding_left),
            },
          },
        });
        if (pageGroup?.type !== data.type?.value) {
          if (pageGroup?.items?.length > 0) {
            await removeGroupElement({
              variables: {
                id: pageGroup?.items[0].id,
              },
            });
          }
        }

        toast({
          title: t('success'),
          description: t('groupEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
              type: data.type?.value,
              padding_top: Number(data.padding_top),
              padding_bottom: Number(data.padding_bottom),
              padding_right: Number(data.padding_right),
              padding_left: Number(data.padding_left),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: t('success'),
          description: t('groupCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(t('confirmDeleteGroup'));
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: t('success'),
          description: t('groupDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack
        as="form"
        spacing="8"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <SimpleGrid minHeight="24" minChildWidth="200px" spacing="8" w="100%">
          <Input
            label={t('title')}
            error={errors?.name as FieldError | undefined}
            {...register('name')}
            isRequired
          />
          <Select
            name="type"
            control={control}
            menuPosition="absolute"
            isRequired
            label={t('type')}
            error={errors.type as FieldError | undefined}
            options={redirectTitleTypes}
            onChange={() => {
              setValue('type', '');
            }}
          />
          <Input
            label={t('heightPx')}
            type="number"
            error={errors?.height as FieldError | undefined}
            {...register('height')}
          />
        </SimpleGrid>
        <Flex gridGap={6} w="100%" flexWrap="wrap">
          <Input
            label={t('paddingLeft')}
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.padding_left as FieldError | undefined}
            {...register('padding_left')}
          />
          <Input
            label={t('paddingRight')}
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_right as FieldError | undefined}
            {...register('padding_right')}
          />
          <Input
            label={t('paddingTop')}
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_top as FieldError | undefined}
            {...register('padding_top')}
          />
          <Input
            label={t('paddingBottom')}
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_bottom as FieldError | undefined}
            {...register('padding_bottom')}
          />
        </Flex>
        <Flex gridGap={6} w="100%" flexWrap="wrap">
          <Input
            label={t('borderTopLeft')}
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.border_left as FieldError | undefined}
            {...register('border_left', { valueAsNumber: true })}
          />
          <Input
            label={t('borderTopRight')}
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.border_right as FieldError | undefined}
            {...register('border_right', { valueAsNumber: true })}
          />
          <Input
            label={t('borderBottomRight')}
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.border_top as FieldError | undefined}
            {...register('border_top', { valueAsNumber: true })}
          />
          <Input
            label={t('borderBottomLeft')}
            type="number"
            width="23%"
            placeholder="px"
            error={errors?.border_bottom as FieldError | undefined}
            {...register('border_bottom', { valueAsNumber: true })}
          />
        </Flex>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            {t('schedule')}
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label={t('scheduleStart')}
                showTimeSelect
                error={errors?.schedule_start as FieldError | undefined}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label={t('scheduleEnd')}
                showTimeSelect
                error={errors?.schedule_end as FieldError | undefined}
              />
            </SimpleGrid>
            <Checkbox colorScheme="brand" checked {...register('countdown')}>
              {t('countdown')}
            </Checkbox>
          </>
        )}
      </VStack>
      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              {t('remove')}
            </Button>
          )}
          <Button onClick={onClose}>{t('close')}</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading || removeLoading}
            onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
          >
            {t('save')}
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
